// @ts-nocheck
import React, { forwardRef, useId, useState, useEffect, useRef, useCallback } from "react";
import "./index.css";
import {
  SlickgridReact,
  SlickRowDetailView,
  ExtensionName,
  type SlickgridReactInstance,
  type GridOption,
  type Column,
} from "slickgrid-react";
import "@slickgrid-universal/common/dist/styles/css/slickgrid-theme-material.css";
import PagerComponent from "./Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import Shimmer from "../../../components/Queries/Shimmer";
import { ExcelExportService } from "@slickgrid-universal/excel-export";
import ToolBar, { ToolbarItem } from "./ToolBar";
import FilterComponent from "./FilterComponent";
import axiosWrapper from "../../../services/axiosWrapper";
import { createRoot } from "react-dom/client";
import Text from "../Text/Text";
import get from "lodash/get";
import debounce from "lodash/debounce";
import dayjs, { Dayjs } from "dayjs";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Formatter } from "@slickgrid-universal/common";
import { cloneDeep } from "lodash";

type SlickEventArgs = Record<string, unknown>;
type FilterType = "Text" | "Date" | "Number" | "MultiSelect";
type RowId = string | number;
type ParentId = string | number | null;

export type GridRowBase = {
  id: RowId;
  isRowDisabled?: boolean;
  __isDummy?: boolean;
  __parentId?: string;
  disableBorder?: boolean;
  __hasChildren?: boolean;
  __collapsed?: boolean;
  parentId?: RowId | null;
  indentLevel?: number;
  treeLevel?: number;
  hasChild?: boolean;
  isExpanded?: boolean;
  _id?: RowId;
  [k: string]: any;
};

export interface ColumnHeaderButton {
  cssClass?: string;
  command?: string;
  tooltip?: string;
}

export interface ColumnHeader {
  buttons?: ColumnHeaderButton[];
}

export interface ColumnDefinition<T extends GridRowBase = GridRowBase> {
  id: string | number;
  field?: string;
  name?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  filterable?: boolean;
  groupable?: boolean;
  type?: string;
  header?: ColumnHeader;
  wrapText?: boolean;
  filterType?: FilterType;
  excelExportOptions?: any;
  headerTemplate?: (args: { column: ColumnDefinition<T>; onClick: (e: unknown) => void }) => React.ReactNode;
  formatter?: (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: T, grid?: any) => unknown;
  editorTemplate?: (args: { value: unknown; updatedColumn: ColumnDefinition<T>; args: T }) => React.ReactNode;
  _valueGetter?: (row: T) => unknown;
}

export interface QueryPredicate {
  field: string;
  operator:
    | "equal"
    | "greaterthan"
    | "greaterthanorequal"
    | "lessthan"
    | "lessthanorequal"
    | "notequal"
    | "null"
    | "notnull"
    | "startswith"
    | "endswith"
    | "contains"
    | "doesnotstartwith"
    | "doesnotendwith"
    | "doesnotcontain"
    | "like"
    | "empty"
    | "notempty"
    | "between";
  value: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
  [k: string]: any;
}

export interface QueryShape {
  where?: Array<QueryPredicate | Record<string, unknown>>;
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
  sorted?: Array<{
    name: string;
    direction: "ascending" | "descending";
  }>;
  nextPageToken?: string | null;
  [k: string]: any;
}

export interface ExcelExportConfig {
  filename?: string;
  sheetName?: string;
  exportWithFormatter?: boolean;
}

export interface SlickGridProps<T extends GridRowBase = GridRowBase>
  extends Required<Pick<SlickGridProps<T>, "columnDefinitions">> {
  id?: string;
  showPageCount?: boolean;
  infiniteScroll?: boolean;
  showLoader?: boolean;
  disableHeader?: boolean;
  disableMarginForNoData?: boolean;
  enableWrapText?: boolean;
  disableRepaintOnDataChange?: boolean;
  dataset?: T[];
  datasetHierarchical?: T[];
  surpassedDataset?: T[];
  columnDefinitions: ColumnDefinition<T>[];
  query?: QueryShape;
  beforeDataBound?: (resp: unknown) => T[];
  remoteDataSource?: string;
  allowPagination?: boolean;
  allowGrouping?: boolean;
  gridOptions?: Partial<GridOption>;
  enableFiltering?: boolean;
  disableCustomSorting?: boolean;
  disableSorting?: boolean;
  enableColumnChooser?: boolean;
  enableMultiSelect?: boolean;
  onSelectedRowIdsChanged?: (args: SlickEventArgs) => void;
  disabledIds?: Array<RowId>;
  selectCheckBoxOnRowClick?: boolean;
  onGroupChanged?: (args: unknown) => void;
  onExtensionRegistered?: (ext: unknown) => void;
  setGridReady?: (ready: boolean) => void;
  onReactGridCreated?: (instance: SlickgridReactInstance) => void;
  toolbar?: ToolbarItem[];
  toolbarClickHandler?: (cmd: unknown) => void;
  showToolbarBorder?: boolean;
  callbackForVisibleColumnsUpdate?: (columns: ColumnDefinition<T>[]) => void;
  toolbarHeight?: number | string;
  propertiesPanel?: boolean;
  excelExport?: boolean;
  isEditMode?: boolean;
  handleUpdateCurrentView?: (payload: { columns: Array<string | number> }) => void;
  onClick?: (e: SlickEventArgs, args: SlickEventArgs) => void;
  onFilterChange?: (activeKeys: string[], conditions: Record<string, any>) => void;
  treeGrid?: boolean;
  hasChild?: boolean;
  disableRowBorder?: boolean;
  hasChildMapping?: string;
  idMapping?: string;
  parentIdMapping?: string;
  titleFormatter?: Formatter;
  initialSortColumnId?: string | number;
  dropdownColumn?: number;
  disableChildDataFetch?: boolean;
  childGridComponent?: React.ComponentType<any> | React.FC<any> | object;
  dragAndDrop?: boolean;
  disableTreeCollapseOnDataChange?: boolean;
  rowDrop?: (payload: { parentId: ParentId; movedRowData: T }) => Promise<boolean>;
  initialSortDirection?: "ASC" | "DESC";
  enableSelectAllCheckbox?: boolean;
  enablePointerForRows?: boolean;
  preAppliedFilters?: Record<string, any>;
  [k: string]: any;
  enableGridMenu?: boolean;
  enableColumnPicker?: boolean;
}

type SlickgridExtras<T extends GridRowBase> = {
  addRecord?: (rowToAdd: T, position?: number) => void;
  removeRecord?: (position?: number) => void;
  refresh?: () => void;
  resetPagination?: (skipApiCall?: boolean) => void;
  appliedFilters?: string[];
  deleteRow?: boolean;
  actual?: unknown;
};
type SlickgridReactInstanceX<T extends GridRowBase> = SlickgridReactInstance & SlickgridExtras<T>;

const NO_OF_FETCHED_ROWS = 1000000;
const FETCH_SIZE = 30;
const ROW_HEIGHT_DEFAULT = 24;
const WRAP_UPDATE_RESIZE_DEBOUNCE_MS = 150;
const INITIAL_WRAP_RUN_DELAY_MS = 2000;
const DRAG_TOLERANCE_PX = 10;
const CHAR_WIDTH_RATIO = 29 / 233;

const DEFAULT_FILTER_OPTIONS: Record<
  Exclude<FilterType, "MultiSelect">,
  { option: QueryPredicate["operator"]; text: unknown }
> = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: dayjs() } },
  Number: { option: "equal", text: 0 },
};

function isNil(v: unknown): v is null | undefined {
  return v === null || v === undefined;
}

function toLowerString(v: unknown): string {
  if (v == null) return "";

  switch (typeof v) {
    case "string":
    case "number":
    case "boolean":
      return String(v).toLowerCase();

    case "object":
      try {
        return JSON.stringify(v).toLowerCase();
      } catch {
        return "";
      }

    default:
      return "";
  }
}

function makeDebounce<TF extends (...args: any[]) => void>(fn: TF, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<TF>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  (debounced as any).cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
  };
  return debounced as TF & { cancel: () => void };
}

type RowSpanResult<T> = {
  newDataset: Array<T & GridRowBase>;
  metadata: Record<number, { columns: Record<string | number, { rowspan: number }> }>;
};

function getRowElementByIndex(index: number) {
  const odd = document.querySelectorAll(".slick-row.odd");
  const even = document.querySelectorAll(".slick-row.even");
  return index % 2 === 0 ? even[index / 2] : odd[Math.floor(index / 2)];
}

function isMidpointHit(dragTop: number, rowEl: Element | null): boolean {
  if (!rowEl) return false;
  const rect = (rowEl as HTMLElement).getBoundingClientRect();
  const midY = rect.top + rect.height / 2;
  return dragTop >= midY - DRAG_TOLERANCE_PX && dragTop <= midY + DRAG_TOLERANCE_PX;
}

function getItemsSourceSafe(dv: any): any[] {
  if (typeof dv?.getFilteredItems === "function") return dv.getFilteredItems();
  if (typeof dv?.getItems === "function") return dv.getItems();
  return [];
}

function getDroppedRowIndex(insertBefore: number): number | null {
  const idx = insertBefore - 1;
  return idx >= 0 ? idx : null;
}

function getRowElementsSafe(movedIdx: number, droppedIdx: number) {
  const movedEl = getRowElementByIndex(movedIdx);
  const droppedEl = getRowElementByIndex(droppedIdx);
  if (!movedEl || !droppedEl) return null;
  return { movedEl, droppedEl };
}

function shouldHideGuide(opts: {
  movedElTop: number;
  rowRectTop: number;
  droppedRowData: any;
  movedRowData: any;
  currentTop: number;
  droppedEl: Element;
  insertBefore: number | null;
}): boolean {
  const { movedElTop, rowRectTop, droppedRowData, movedRowData, currentTop, droppedEl, insertBefore } = opts;
  if (movedElTop < rowRectTop) return true;
  if (!droppedRowData) return true;
  const differentParent = droppedRowData.parentId !== movedRowData.parentId;
  const midpointHit = isMidpointHit(currentTop, droppedEl);
  if (differentParent && !midpointHit && insertBefore !== 0) return true;
  return false;
}

function setGuide(reorderGuide: HTMLElement, show: boolean, highlight: boolean) {
  reorderGuide.style.display = show ? "block" : "none";
  if (!show) {
    reorderGuide.classList.remove("parent-child-move");
    return;
  }
  if (highlight) reorderGuide.classList.add("parent-child-move");
  else reorderGuide.classList.remove("parent-child-move");
}

const mapSpecialColumn = <T extends GridRowBase>(c: any): ColumnDefinition<T> => {
  return {
    ...c,
    sortable: false,
    filterable: false,
    groupable: false,
    reorderable: false,
    selectable: false,
  };
};

const SlickGridInner = forwardRef(function SlickGridInner<T extends GridRowBase = GridRowBase>(
  props: SlickGridProps<T>,
  ref: any,
) {
  const { t } = useTranslation();
  const { showPageCount = true } = props;

  const generatedId = useId();
  const id = props.id || generatedId;
  const standByRef = useRef<any>(null);

  const gridRef = ref || standByRef;

  const excelExportService = React.useMemo(() => new ExcelExportService(), []);
  useEffect(() => {
    (globalThis as any).excelExportService = excelExportService;
  }, [excelExportService]);

  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [filterConditions, setFilterConditions] = useState<Record<string, any>>({});
  const [loaderOnPageChange, setLoaderOnPageChange] = useState<boolean>(!!props.showLoader);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItemCount, setTotalItemCount] = useState<number>(1);
  const [allPrerequisiteLoaded, setAllPrerequisiteLoaded] = useState<boolean>(false);
  const [cssClasses, setCssClasses] = useState<string>("");

  const isEditModeRef = useRef<boolean>(false);
  const nextPageTokenRef = useRef<string | null>(null);
  const isLastRef = useRef<boolean>(false);

  const [loaderForInfiniteScroll, setLoaderForInfiniteScroll] = useState<boolean>(false);
  const [currentLoadedCount, setCurrentLoadedCount] = useState<number>(0);

  console.log(Object.keys(props ?? {}).length);

  useEffect(() => {
    isEditModeRef.current = !!props.isEditMode;
  }, [props.isEditMode]);

  const [totalPages, setTotalPages] = useState<number>(
    Math.ceil(totalItemCount / pageSize) === 0 ? 1 : Math.ceil(totalItemCount / pageSize),
  );
  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(totalItemCount / pageSize)));
  }, [pageSize, totalItemCount]);
  useEffect(() => {
    setCurrentPage(1);
  }, [totalPages]);

  const [dataset, setDataset] = useState<T[]>([]);
  const [updatedColumnDefinitions, setUpdatedColumnDefinitions] = useState<ColumnDefinition<T>[]>([]);
  const [sortingDetails, setSortingDetails] = useState<false | { columnId: string; sortAsc: boolean }>(false);

  const [currentColumnId, setCurrentColumnId] = useState<string>("");
  const [filterPredicates, setFilterPredicates] = useState<Record<string, unknown> | string>("");
  const [anchorEl, setAnchorEl] = useState<{ top: number; left: number } | null>(null);
  const [filterType, setFilterType] = useState<FilterType | "">("");
  const [filterOpened, setFilterOpened] = useState<boolean>(false);
  const [excelExportExtension, setExcelExportExtension] = useState<ExcelExportService | null>(null);

  const topOfDragRowRef = useRef<number | undefined>(undefined);
  const [insertBefore, setInsertBefore] = useState<number | null>(null);
  const [dataRow, setDataRow] = useState<number | null>(null);
  const [top, setTop] = useState<number | null>(null);

  const stateRef = useRef<Record<string, unknown> | undefined>(undefined);
  const [gridMounted, setGridMounted] = useState<boolean>(false);

  const specialColumnRef = useRef<ColumnDefinition<T>[]>([]);
  const [selectedItems, setSelectedItems] = useState<number>(0);

  useEffect(() => {
    const cls: string[] = [];
    if (props.disableHeader) cls.push("disable-header");
    if (props.disableMarginForNoData) cls.push("disable-margin-no-data");
    if (props.enableSelectAllCheckbox && dataset.length > 0) cls.push("enable-selectall-checkbox");
    if (props.enableWrapText) cls.push("enable-wrap-text");
    if (props.disableRowBorder) cls.push("disable-row-border");
    if (props.enablePointerForRows) cls.push("cursor-pointer-row");
    setCssClasses(cls.join(" "));
  }, [
    props.disableHeader,
    props.disableMarginForNoData,
    props.enableSelectAllCheckbox,
    props.enableWrapText,
    props.disableRowBorder,
    props.enablePointerForRows,
    dataset,
  ]);

  useEffect(() => {
    if (dataset && props.columnDefinitions.length) {
      setAllPrerequisiteLoaded(true);
    }
  }, [dataset, props.columnDefinitions]);

  useEffect(() => {
    if (props.infiniteScroll && dataset) {
      setCurrentLoadedCount(dataset?.length);
    }
  }, [dataset, props.infiniteScroll]);

  useEffect(() => {
    stateRef.current = {
      pageSize,
      currentPage,
      query: props.query,
      originalDataset: props.dataset,
      sortingDetails,
      appliedFilters,
      dataset,
      filterConditions,
      filterPredicates,
      currentColumnId,
      totalItemCount,
    };
  });

  useEffect(() => {
    return () => {
      gridRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      (globalThis as any).gridRef1 = gridRef;
      return () => {
        (globalThis as any).gridRef1 = undefined;
      };
    }
    return () => undefined;
  }, [gridRef]);

  useEffect(() => {
    const current = gridRef.current;
    if (current) {
      current.addRecord = (rowToAdd: T, position?: number) => {
        const currentItems = [...(current.dataView.getItems() as T[])];
        const finalPosition =
          position === undefined || position < 0 || position > currentItems.length ? currentItems.length : position;

        const updatedItems = [...currentItems.slice(0, finalPosition), rowToAdd, ...currentItems.slice(finalPosition)];
        setDataset(updatedItems);

        setTimeout(() => {
          current.slickGrid?.setSelectedRows([finalPosition]);
        }, 0);
      };

      current.removeRecord = (position?: number) => {
        if (position === undefined) return;
        const updatedItems = cloneDeep(current.dataView.getItems() as T[]);
        if (position >= 0 && position < updatedItems.length) {
          updatedItems.splice(position, 1);
          setDataset(updatedItems);
        }
      };

      current.refresh = () => {
        current.slickGrid?.setSelectedRows([]);
        handleDataLoad(); // Use the existing data loading function
      };

      current.resetPagination = (skipApiCall = false) => {
        nextPageTokenRef.current = null;
        isLastRef.current = false;
        setCurrentPage(1); // Always reset page to 1
        if (!skipApiCall) {
          handleDataLoad();
        }
      };
    }
  }); // Note: An empty dependency array would be better, but matching JS means it runs on every render.

  const ellipseFormatterWithTooltip = (args: { value: unknown; column: ColumnDefinition<T> }) => {
    const cellValue = (args.value ?? "") as string | number;
    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.overflow = "hidden";
    const root = createRoot(container);
    root.render(<Text tooltipContent={String(cellValue)}>{String(cellValue)}</Text>);
    return container;
  };

  function buildBaseFormatter(column: ColumnDefinition<T>, isEditMode: boolean) {
    if (!column.formatter && !(isEditMode && column.editorTemplate)) {
      return (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: T) => {
        if ((dataContext as any).__isDummy) return "";
        return column._valueGetter
          ? column._valueGetter(dataContext)
          : (get(dataContext as any, column.field as any, "") as unknown);
      };
    }
    if (column.formatter && !(isEditMode && column.editorTemplate)) {
      const self = column.formatter;
      return (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: T, grid?: any) => {
        if ((dataContext as any)?.__isDummy) return "";
        if (typeof self === "function") {
          return self(row, cell, value, columnDef, dataContext, grid);
        } else {
          console.log("Formatter is not a function");
          return "";
        }
      };
    }

    return undefined;
  }

  function getTextLines(text: string, columnWidth: number): number {
    const charsPerLine = Math.max(1, columnWidth * CHAR_WIDTH_RATIO);
    return Math.ceil(text.length / charsPerLine);
  }

  function getRenderedLines(
    slickGrid: any,
    dataView: any,
    itemId: string | number | undefined,
    colIndex: number,
    rowHeight: number,
    defaultLines: number,
  ): number {
    if (itemId == null) return defaultLines;
    const renderedRowIndex = dataView.getRowById(String(itemId));
    if (renderedRowIndex == null || renderedRowIndex < 0) return defaultLines;

    const cell = slickGrid.getCellNode(renderedRowIndex, colIndex);
    const requiredHeight = cell?.firstChild ? (cell.firstChild as HTMLElement).clientHeight : 0;
    return requiredHeight ? Math.ceil(requiredHeight / rowHeight) * 2 : defaultLines;
  }

  function calculateMaxLinesForItem<T extends GridRowBase>(
    item: T,
    columnDefs: ColumnDefinition<T>[],
    slickGrid: any,
    dataView: any,
    rowHeight: number,
  ): number {
    let maxLines = 1;
    for (const [colIndex, col] of columnDefs.entries()) {
      const fieldKey = col.field;
      const value = fieldKey ? get(item as any, fieldKey) : "";
      const text = String(value ?? "").trim();
      const columnWidth = col.width || 100;

      const defaultLines = getTextLines(text, columnWidth);
      const totalLines = getRenderedLines(slickGrid, dataView, (item as any).id, colIndex, rowHeight, defaultLines);
      maxLines = Math.max(maxLines, totalLines);
    }
    return maxLines;
  }

  function createMainRow<T extends GridRowBase>(item: T, idxCompressed: number, requiredRows: number): T & GridRowBase {
    const rowId = (item as any).id ?? `row_${idxCompressed}`;
    return {
      ...item,
      id: rowId,
      disableBorder: requiredRows > 1,
    };
  }

  function createDummyRows<T extends GridRowBase>(
    item: T,
    mainRowId: string,
    requiredRows: number,
  ): Array<T & GridRowBase> {
    const dummies: Array<T & GridRowBase> = [];
    for (let iIdx = 1; iIdx < requiredRows; iIdx++) {
      dummies.push({
        ...item,
        __isDummy: true,
        __parentId: String(mainRowId),
        id: `${mainRowId}_dummy_${iIdx}_${crypto.randomUUID()}`,
      });
    }
    return dummies;
  }

  function buildMetadataForRow<T extends GridRowBase>(columnDefs: ColumnDefinition<T>[], requiredRows: number): any {
    const columnsMeta: Record<string, any> = {};
    for (const col of columnDefs) {
      columnsMeta[col.id] = { rowspan: requiredRows };
    }
    return { columns: columnsMeta };
  }

  function calculateRowSpans<T extends GridRowBase>(
    dataArr: T[],
    columnDefs: ColumnDefinition<T>[],
    slickGrid: any,
    dataView: any,
    rowHeight: number,
  ): RowSpanResult<T> {
    const metadata: RowSpanResult<T>["metadata"] = {};
    const newDataset: Array<T & GridRowBase> = [];

    for (const [idxCompressed, item] of dataArr.entries()) {
      if ((item as any).__isDummy) continue;

      const maxLines = calculateMaxLinesForItem(item as GridRowBase, columnDefs as any, slickGrid, dataView, rowHeight);
      const requiredRows = Math.ceil(maxLines / 2);
      const mainRow = createMainRow(item as GridRowBase, idxCompressed, requiredRows);
      const mainRowIndex = newDataset.length;

      newDataset.push(mainRow as any);

      if (requiredRows > 1) {
        metadata[mainRowIndex] = buildMetadataForRow(columnDefs, requiredRows);
        const dummyRows = createDummyRows(item as GridRowBase, mainRow.id as string, requiredRows);
        newDataset.push(...(dummyRows as T[]));
      }
    }

    return { newDataset, metadata };
  }

  const handleWrapUpdate = () => {
    const current = gridRef.current;
    if (!current) return;

    const slickGrid = current.slickGrid;
    const dataView = current.dataView;
    const columns = slickGrid.getColumns() as ColumnDefinition<T>[];
    const rawItems = (dataView.getItems() as T[]).filter((it: any) => !it.__isDummy);

    const rowHeight = (slickGrid as any).getRowHeight?.() ?? ROW_HEIGHT_DEFAULT;
    const { newDataset, metadata } = calculateRowSpans(rawItems, columns, slickGrid, dataView, rowHeight);

    requestAnimationFrame(() => {
      dataView.beginUpdate();
      dataView.setItems(newDataset as T[]);
      dataView.endUpdate();

      dataView.getItemMetadata = (rowIndex: number) => {
        const item = dataView.getItem(rowIndex);
        if (!item) return null;

        const cssClasses: string[] = [];
        if (item.__isDummy) cssClasses.push("dummy-row");
        if (item.disableBorder) cssClasses.push("disable-border");

        return {
          ...metadata[rowIndex],
          cssClasses: cssClasses.join(" "),
        };
      };

      slickGrid.invalidate();
      slickGrid.render();
    });

    setTimeout(() => {
      if (props.treeGrid) {
        gridRef.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (!props.disableRepaintOnDataChange && gridRef.current) {
      gridRef.current?.slickGrid?.invalidate();
    }
  }, [dataset]);

  useEffect(() => {
    if (!props.enableWrapText) return;
    const sg = gridRef?.current?.slickGrid;
    const dataView = gridRef?.current?.dataView;
    if (!sg) return;
    const originalFilter = dataView.getFilter();
    dataView.setFilter((item: any) => {
      if (item.__isDummy) return true;
      return originalFilter ? originalFilter(item) : true;
    });

    handleWrapUpdate();
    const onResize = makeDebounce(handleWrapUpdate, WRAP_UPDATE_RESIZE_DEBOUNCE_MS);
    const timer = setTimeout(() => handleWrapUpdate(), INITIAL_WRAP_RUN_DELAY_MS);

    sg.onColumnsResized.subscribe(onResize);
    return () => {
      clearTimeout(timer);
      sg.onColumnsResized.unsubscribe(onResize);
      (onResize as any).cancel?.();
    };
  }, [props.enableWrapText, dataset]);

  const matchDateFilter = (fieldValue: any, filterOption: QueryPredicate["operator"], filterValue: any) => {
    const format = "MMM D,YYYY [at] hh:mm:ss A";
    const itemDate = Number.isNaN(Number(fieldValue)) ? dayjs(String(fieldValue), format) : dayjs(Number(fieldValue));
    const filterDate = dayjs(filterValue?.date ? (filterValue.date as Dayjs) : filterValue);

    switch (filterOption) {
      case "equal":
        return itemDate.isSame(filterDate, "day");
      case "greaterthan":
        return itemDate.isAfter(filterDate, "day");
      case "greaterthanorequal":
        return itemDate.isSame(filterDate, "day") || itemDate.isAfter(filterDate, "day");
      case "lessthan":
        return itemDate.isBefore(filterDate, "day");
      case "lessthanorequal":
        return itemDate.isSame(filterDate, "day") || itemDate.isBefore(filterDate, "day");
      case "notequal":
        return !itemDate.isSame(filterDate, "day");
      case "null":
        return !fieldValue || String(fieldValue).trim() === "";
      case "notnull":
        return !!fieldValue && String(fieldValue).trim() !== "";
      default:
        return false;
    }
  };

  const matchTextFilter = (fieldValue: any, filterOption: QueryPredicate["operator"], filterValue: any) => {
    const fieldValueStr = toLowerString(fieldValue);
    const filterValueLower = toLowerString(filterValue);

    switch (filterOption) {
      case "startswith":
        return fieldValueStr.startsWith(filterValueLower);
      case "endswith":
        return fieldValueStr.endsWith(filterValueLower);
      case "empty":
        return fieldValueStr === "";
      case "equal":
        return fieldValueStr === filterValueLower;
      case "contains":
      case "like":
        return fieldValueStr.includes(filterValueLower);
      case "doesnotstartwith":
        return !fieldValueStr.startsWith(filterValueLower);
      case "doesnotendwith":
        return !fieldValueStr.endsWith(filterValueLower);
      case "doesnotcontain":
        return !fieldValueStr.includes(filterValueLower);
      case "notequal":
        return fieldValueStr !== filterValueLower;
      case "notnull":
      case "notempty":
        return fieldValueStr !== "";
      default:
        return false;
    }
  };

  const matchNumberFilter = (fieldValue: any, filterOption: QueryPredicate["operator"], filterValue: any) => {
    const n = Number(fieldValue);
    const f = Number(filterValue);
    switch (filterOption) {
      case "equal":
        return n === f;
      case "greaterthan":
        return n > f;
      case "greaterthanorequal":
        return n >= f;
      case "lessthan":
        return n < f;
      case "lessthanorequal":
        return n <= f;
      case "notequal":
        return n !== f;
      case "null":
        return Number.isNaN(n);
      case "notnull":
        return !Number.isNaN(n);
      default:
        return false;
    }
  };

  const matchMultiSelectFilter = (fieldValue: any, filterValue: any) => {
    const selectedValues = Array.isArray(filterValue) ? filterValue : [filterValue];
    if (Array.isArray(fieldValue)) {
      const names = fieldValue.map((it) => it?.name ?? String(it));
      return selectedValues.some((sv) => names.some((fv) => toLowerString(fv) === toLowerString(sv)));
    }
    return selectedValues.some((sv) => toLowerString(fieldValue) === toLowerString(sv));
  };

  function applyFilterLocal(
    dataArr: T[],
    columnId: string,
    filterOption: QueryPredicate["operator"],
    filterValue: any,
    fType: FilterType,
  ): T[] {
    return dataArr.filter((item) => {
      const fieldValue = get(item as any, columnId) ?? "";

      switch (fType) {
        case "Date":
          return matchDateFilter(fieldValue, filterOption, filterValue);
        case "Text":
          return matchTextFilter(fieldValue, filterOption, filterValue);
        case "Number":
          return matchNumberFilter(fieldValue, filterOption, filterValue);
        case "MultiSelect":
          return matchMultiSelectFilter(fieldValue, filterValue);
        default:
          return false;
      }
    });
  }

  const getUpdatedFilterConditions = (conds: Record<string, any>) => {
    const updated = Object.keys(conds).reduce<Record<string, any>>((result, filterKey) => {
      const filterValue = conds[filterKey];
      if (filterValue.option === "between" && filterValue.type === "Date") {
        const { text, ...otherFields } = filterValue;
        const { startDate, endDate } = text || {};
        if (startDate) {
          result[`${filterKey}_start`] = {
            ...otherFields,
            option: "greaterthanorequal",
            text: startDate.format(),
          };
        }
        if (endDate) {
          result[`${filterKey}_end`] = {
            ...otherFields,
            option: "lessthanorequal",
            text: endDate.format(),
          };
        }
      } else if (filterValue.type === "Date" && (filterValue.option === "equal" || !filterValue.option)) {
        const { text, ...otherFields } = filterValue;
        const dayjsObject = dayjs();
        const startDate = (text?.date ?? dayjsObject).startOf("day").format();
        const endDate = (text?.date ?? dayjsObject).endOf("day").format();
        result[`${filterKey}_start`] = { ...otherFields, option: "greaterthanorequal", text: startDate };
        result[`${filterKey}_end`] = { ...otherFields, option: "lessthanorequal", text: endDate };
      } else {
        result[filterKey] = filterValue;
      }
      return result;
    }, {});
    return updated;
  };

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    if (!grid) return;

    const renderHeaderTemplate = (_e: any, args: any) => {
      const col = args.column as ColumnDefinition<T>;
      if (!col?.headerTemplate) return;

      args.node.innerHTML = "";
      const mount = document.createElement("div");
      args.node.appendChild(mount);
      const root = createRoot(mount);
      root.render(
        col.headerTemplate({
          column: col,
          onClick: (_ev: unknown) => {},
        }),
      );
    };

    grid.onHeaderCellRendered.subscribe(renderHeaderTemplate);
    grid.setColumns(grid.getColumns());
    grid.invalidate();
    grid.render();
    return () => {
      grid.onHeaderCellRendered.unsubscribe(renderHeaderTemplate);
    };
  }, [props.columnDefinitions]);

  const ensureIds = React.useCallback(
    (arr: T[]): T[] =>
      arr.map((data) => {
        const out: T = { ...data };

        const isIdMissing = out.id === undefined || out.id === null;

        if (isIdMissing) {
          const mappedId = props.idMapping ? (out as any)[props.idMapping] : undefined;
          const altId = (out as any)._id;

          if (mappedId) {
            out.id = mappedId;
          } else if (altId == null) {
            out.id = `row_${crypto.randomUUID()}`;
          } else {
            out.id = altId as RowId;
          }
        }

        return out;
      }),
    [props.idMapping],
  );

  const mapTreeGridFlags = React.useCallback(
    (arr: T[]) =>
      props.treeGrid
        ? arr.map((d) => {
            const hasChild = props.hasChildMapping ? Boolean((d as any)[props.hasChildMapping]) : false;
            return {
              ...d,
              __hasChildren: hasChild,
              __collapsed: hasChild ? true : undefined,
            } as T;
          })
        : arr,
    [props.treeGrid, props.hasChildMapping],
  );

  const buildModifiedQuery = (skip: number, take: number): QueryShape => {
    const modifiedQuery: QueryShape = { ...props.query, skip, take, requiresCounts: true };

    const hasPredicates =
      filterPredicates && typeof filterPredicates === "object" && Object.keys(filterPredicates).length > 0;

    if (hasPredicates) {
      if (props.query?.where?.length) {
        modifiedQuery.where = [...(props.query.where || []), filterPredicates];
      } else {
        modifiedQuery.where = [filterPredicates];
      }
    }
    if (sortingDetails) {
      modifiedQuery.sorted = [
        { name: sortingDetails.columnId, direction: sortingDetails.sortAsc ? "ascending" : "descending" },
      ];
    }
    if (nextPageTokenRef.current) {
      modifiedQuery.nextPageToken = nextPageTokenRef.current;
    }
    return modifiedQuery;
  };

  const processRemoteResponse = (response: any): T[] => {
    const current = gridRef.current;
    if (current) current.actual = response;

    let modifiedResponse: T[];
    if (props.beforeDataBound) {
      modifiedResponse = props.beforeDataBound(response);
    } else {
      modifiedResponse = response?.data?.result as T[];
    }
    if (response?.data?.nextPageToken) nextPageTokenRef.current = response.data.nextPageToken;
    if (!isNil(response?.data?.isLast)) isLastRef.current = Boolean(response.data.isLast);
    if (!isNil(response?.data?.count)) setTotalItemCount(Number(response.data.count));
    return modifiedResponse;
  };

  const processLocalDataset = (skip: number, take: number): T[] => {
    const updatedFilterConds = getUpdatedFilterConditions(filterConditions);

    const applyFiltersToData = (arr: T[]) => {
      if (!appliedFilters.length) return arr;
      return Object.keys(updatedFilterConds).reduce((currentData, filter) => {
        const cond = updatedFilterConds[filter];
        return applyFilterLocal(
          currentData,
          cond.id,
          cond.option || DEFAULT_FILTER_OPTIONS[cond.type as keyof typeof DEFAULT_FILTER_OPTIONS].option,
          cond.text,
          cond.type,
        );
      }, arr);
    };

    const includeMissingParentsForTree = (filteredData: T[], items: T[]) => {
      if (!props.treeGrid) return filteredData;
      const parentKey = props.parentIdMapping as keyof T;
      const idMap = new Map(items.map((it) => [(it as any).id, it]));
      const neededIds = new Set(filteredData.map((d: any) => d.id));

      const buildParentChain = (child: any) => {
        const chain: any[] = [];
        let current = child;
        while (current?.[parentKey]) {
          const parentId = current[parentKey as string];
          if (neededIds.has(parentId)) break;
          const parent = idMap.get(parentId);
          if (!parent) break;
          neededIds.add(parentId);
          chain.push(parent);
          current = parent;
        }
        return chain;
      };

      const appendUnique = (target: any[], seenSet: Set<any>, toAppend: any[]) => {
        for (let i = toAppend.length - 1; i >= 0; i--) {
          const p = toAppend[i];
          if (!seenSet.has(p.id)) {
            target.push(p);
            seenSet.add(p.id);
          }
        }
      };

      const missingParentData: any[] = [];
      const addedParentIds = new Set();

      for (const child of filteredData as any[]) {
        const chain = buildParentChain(child);
        appendUnique(missingParentData, addedParentIds, chain);
      }

      const out: any[] = [];
      const seen = new Set();
      for (const item of [...missingParentData, ...filteredData] as any[]) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          out.push(item);
        }
      }
      return out as T[];
    };

    let modifiedResponse: T[];
    const filtered = applyFiltersToData(props.dataset || []);

    if (appliedFilters.length) {
      let filteredData = filtered;
      if (props.treeGrid) {
        filteredData = includeMissingParentsForTree(filteredData, dataset);
      }
      modifiedResponse = filteredData;
      setTotalItemCount(filteredData.length);
    } else {
      modifiedResponse = props.dataset || [];
      setTotalItemCount(modifiedResponse.length);
    }

    if (sortingDetails) {
      const col = props.columnDefinitions.find((c) => c.id === sortingDetails.columnId);
      const getter = col?._valueGetter;
      modifiedResponse = [...modifiedResponse].sort((a, b) => {
        const va = getter ? getter(a) : get(a as any, sortingDetails.columnId);
        const vb = getter ? getter(b) : get(b as any, sortingDetails.columnId);
        if (va > vb) return sortingDetails.sortAsc ? 1 : -1;
        if (va < vb) return sortingDetails.sortAsc ? -1 : 1;
        return 0;
      });
    }

    return modifiedResponse.slice(skip, take + skip);
  };

  const loadData = async (skip: number, take: number, appendNext = false) => {
    let modifiedResponse: T[] = [];

    if (props.query && props.remoteDataSource) {
      const modifiedQuery = buildModifiedQuery(skip, take);
      const response = await axiosWrapper.post(props.remoteDataSource, { value: modifiedQuery });
      modifiedResponse = processRemoteResponse(response);
    } else if (props.dataset) {
      modifiedResponse = processLocalDataset(skip, take);
    }

    if (props.treeGrid && modifiedResponse?.length) {
      modifiedResponse = mapTreeGridFlags(modifiedResponse);
    }

    modifiedResponse = ensureIds(modifiedResponse);

    setLoaderOnPageChange(false);

    if (appendNext) {
      setDataset((prev) => [...(prev || []), ...modifiedResponse]);
      setLoaderForInfiniteScroll(false);
    } else {
      setDataset(modifiedResponse);
    }

    if ((gridRef?.current as any)?.slickGrid?.setSelectedRows) {
      (gridRef.current as any).slickGrid.setSelectedRows([]);
    }

    props.setGridReady?.(true);
  };

  const handleDataLoad = () => {
    if (props.allowPagination) {
      loadData((currentPage - 1) * pageSize, pageSize);
    } else if (props.infiniteScroll) {
      loadData(0, FETCH_SIZE);
    } else {
      loadData(0, NO_OF_FETCHED_ROWS);
    }
  };

  useEffect(() => {
    setLoaderOnPageChange(true);
    const timeout = setTimeout(() => {
      handleDataLoad();
    }, 0);
    return () => clearTimeout(timeout);
  }, [pageSize, currentPage, props.query, props.dataset, sortingDetails, appliedFilters]);

  useEffect(() => {
    if (props.treeGrid && !props.disableTreeCollapseOnDataChange) {
      gridRef.current?.treeDataService?.toggleTreeDataCollapse?.(false);
    }
  }, [dataset, props.treeGrid, props.disableTreeCollapseOnDataChange, gridRef]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const keysToStop = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (keysToStop.includes(e.code)) {
      e.stopPropagation();
    }
  };

  const handleOnScroll = debounce(($event: any) => {
    const args = $event.detail.args;
    const viewportElm = args.grid.getViewportNode();

    if (
      ["mousewheel", "scroll"].includes(args.triggeredBy || "") &&
      viewportElm.scrollTop > 0 &&
      Math.ceil(viewportElm.offsetHeight + args.scrollTop) >= args.scrollHeight
    ) {
      const startIdx = gridRef.current?.dataView?.getItemCount() || 0;
      const st = stateRef.current as any;
      if (st.totalItemCount >= st.dataset.length) {
        if (!isLastRef.current) {
          setLoaderForInfiniteScroll(true);
          loadData(startIdx, FETCH_SIZE, true);
        }
      }
    }
  }, 200);

  useEffect(() => {
    const renderEditorTemplate = (updatedColumn: ColumnDefinition<T>, args: T) => {
      const value = updatedColumn._valueGetter
        ? updatedColumn._valueGetter(args)
        : get(args as any, updatedColumn.field as any);
      const container = document.createElement("div");
      container.classList.add("w-full");
      const root = createRoot(container);
      root.render(
        <Box className="w-full h-full flex" onKeyDown={handleKeyPress}>
          {updatedColumn.editorTemplate?.({ value, updatedColumn, args })}
        </Box>,
      );
      return container;
    };

    const columnDefinitions = props.columnDefinitions.map((column) => {
      let updated: ColumnDefinition<T> = {
        ...column,
        sortable: !props.isEditMode,
        excelExportOptions: {
          valueParserCallback: (_: unknown, { dataContext }: { dataContext: T }) =>
            column._valueGetter
              ? column._valueGetter(dataContext)
              : (get(dataContext as any, column.field as any, "") as unknown),
        },
      };

      updated.formatter = buildBaseFormatter(column, !!props.isEditMode);

      if (props.isEditMode && updated.editorTemplate) {
        updated.formatter = (row: number, _col: number, _value: unknown, _columnDef: unknown, args: T, grid: any) => {
          const selectedRow = (gridRef.current?.slickGrid as any)?.selectedRows?.[0];
          if (selectedRow === row) {
            return renderEditorTemplate(updated, args);
          }
          if (column.formatter) {
            return column.formatter(row, _col, _value, _columnDef, args, grid);
          }
          if (column._valueGetter) return column._valueGetter(args);
          return get(args as any, column.field as any);
        };
      }

      if (column.wrapText && !props.isEditMode) {
        updated.formatter = (_row, _col, _value, _columnDef, args: T) => {
          return ellipseFormatterWithTooltip({
            value: column._valueGetter ? column._valueGetter(args) : get(args as any, column.field as any),
            column,
          });
        };
      }

      if (column.filterType && !props.isEditMode) {
        const buttoncssClass = appliedFilters.includes(String(column.id))
          ? "mdi mdi-filter-variant text-[var(--primary-gradient-color1)]"
          : "mdi mdi-filter-variant text-slate-900";

        updated = {
          ...updated,
          filterable: true,
          sortable: true,
          groupable: true,
          type: "string",
          header: {
            ...column.header,
            buttons: [
              {
                cssClass: buttoncssClass,
                command: "toggle-highlight",
                tooltip: String(column.id),
              },
            ],
          },
        };
      }

      return updated;
    });

    if (props.isEditMode) {
      setUpdatedColumnDefinitions(columnDefinitions);
    } else {
      setUpdatedColumnDefinitions([...specialColumnRef.current, ...columnDefinitions]);
    }
  }, [props.columnDefinitions, props.isEditMode, appliedFilters, props.preAppliedFilters]);

  useEffect(() => {
    if (!props.dragAndDrop) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let lastTop: number | null = null;

    const checkPosition = () => {
      const el = document.querySelector(".slick-reorder-proxy");
      if (el) {
        const { top } = (el as HTMLElement).getBoundingClientRect();
        if (top !== lastTop) {
          topOfDragRowRef.current = top;
          setTop(top);
          lastTop = top;
        }
      }
    };

    const observer = new MutationObserver(() => {
      const element = document.querySelector(".slick-reorder-proxy");
      if (element && !intervalId) {
        intervalId = setInterval(checkPosition, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
    };
  }, [props.dragAndDrop]);

  useEffect(() => {
    if (props.enableWrapText && gridRef.current) {
      const sg = gridRef.current.slickGrid;
      if (sg) {
        sg.onColumnsResized.notify({ triggeredByColumn: undefined, grid: sg } as any);
      }
    }
  }, [dataset, props.isEditMode, props.enableWrapText, gridRef]);

  const handleFilterIconClick = (
    event: React.MouseEvent<HTMLElement>,
    columnIdArg: string,
    filterTypeArg: FilterType,
  ) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setFilterOpened(true);
    setAnchorEl({ top: rect.top, left: rect.left });
    setCurrentColumnId(columnIdArg);
    setFilterType(filterTypeArg);
  };

  const onBeforeMoveRows = (_e: any, data: any) => {
    setInsertBefore(data.insertBefore);
    setDataRow(data.rows[0]);

    if (data.rows[0] < data.insertBefore) return false;
    for (const r of data.rows as number[]) {
      if (r === data.insertBefore - 1) {
        _e.stopPropagation();
        return false;
      }
    }
    return true;
  };

  const determineDropTarget = (opts: {
    currentTop: number | undefined;
    insertBeforeIdx: number;
    movedRowElement: Element | null;
    droppedRowElement: Element | null;
    droppedRowData: any;
  }) => {
    const { currentTop, insertBeforeIdx, movedRowElement, droppedRowElement, droppedRowData } = opts;
    let newParentId: ParentId = null;
    let newInsertPosition = insertBeforeIdx;

    if (!droppedRowElement) {
      return { newParentId, newInsertPosition };
    }

    const movedRectTop = (movedRowElement as HTMLElement | null)?.getBoundingClientRect().top ?? -Infinity;
    const rowRect = (droppedRowElement as HTMLElement).getBoundingClientRect();
    const midpointY = rowRect.top + rowRect.height / 2;

    if (movedRectTop < rowRect.top) {
      return { newParentId, newInsertPosition };
    }

    if (isMidpointHit(currentTop ?? -Infinity, droppedRowElement)) {
      newParentId = droppedRowData?._id ?? null;
      newInsertPosition = insertBeforeIdx;
    } else if (
      currentTop !== undefined &&
      currentTop < rowRect.top + rowRect.height &&
      currentTop >= midpointY + DRAG_TOLERANCE_PX
    ) {
      newInsertPosition = insertBeforeIdx;
    }

    return { newParentId, newInsertPosition };
  };

  const reconcileOldParentChildren = (previousParentId: RowId | undefined, treeData: any[]) => {
    if (!previousParentId) return;
    const oldParent = treeData.find((it) => it._id === previousParentId);
    if (!oldParent) return;
    const hasChildren = treeData.some((it) => it.parentId === oldParent._id);
    if (!hasChildren) {
      oldParent.__hasChildren = false;
      oldParent.hasChild = false;
    }
  };

  const tryApplyRowDrop = async (
    newParentId: ParentId,
    movedRowData: any,
    droppedRowData: any,
    previousParentId: RowId | undefined,
    treeData: any[],
  ) => {
    setLoaderOnPageChange(true);
    const response = await props.rowDrop?.({ parentId: newParentId ?? null, movedRowData });
    setLoaderOnPageChange(false);
    if (!response) return false;

    movedRowData.parentId = newParentId;
    movedRowData.treeLevel = newParentId ? (droppedRowData?.treeLevel || 0) + 1 : 0;

    if (newParentId && droppedRowData) {
      droppedRowData.__hasChildren = true;
      droppedRowData.hasChild = true;
    }

    reconcileOldParentChildren(previousParentId, treeData);
    return true;
  };

  const onMoveRows = async (_e: any, args: any) => {
    const { rows, insertBefore: insertBeforeIdx } = args;

    if (gridRef.current) {
      gridRef.current.dataView.sort(() => 0, true);
    }

    const currentTop = topOfDragRowRef.current!;
    const [row] = rows as number[];

    const dv: any = gridRef.current?.dataView;
    const itemsSource = typeof dv?.getFilteredItems === "function" ? dv.getFilteredItems() : (dv?.getItems?.() ?? []);
    const data = [...itemsSource] as any[];
    const movedRowData = data[row];
    const previousParentId = movedRowData.parentId;

    const droppedRowIndex = insertBeforeIdx === 0 ? null : (insertBeforeIdx as number) - 1;
    const droppedRowData = droppedRowIndex == null ? null : data[droppedRowIndex];

    const movedRowElement = getRowElementByIndex(row);
    const droppedRowElement = droppedRowIndex == null ? null : getRowElementByIndex(droppedRowIndex);

    const { newParentId, newInsertPosition } = determineDropTarget({
      currentTop,
      insertBeforeIdx,
      movedRowElement,
      droppedRowElement,
      droppedRowData,
    });

    let treeData = gridRef.current?.dataView?.getItems?.() ?? [];

    if (newParentId || insertBeforeIdx === 0) {
      await tryApplyRowDrop(newParentId ?? null, movedRowData, droppedRowData, previousParentId, treeData);
    }

    treeData = treeData.filter((it: any) => it._id !== movedRowData._id);
    treeData.splice(newInsertPosition, 0, movedRowData);

    setDataset(treeData as T[]);
    gridRef.current?.slickGrid.invalidate();
  };

  const handleDrag = () => {
    if (!gridRef.current) return;

    const reorderGuide = document.querySelector<HTMLElement>(".slick-reorder-guide");
    if (!reorderGuide) return;

    const dv: any = gridRef.current.dataView;
    const itemsSource = getItemsSourceSafe(dv);
    const dataRows = [...itemsSource] as any[];

    const currentTop = topOfDragRowRef.current!;
    const iBefore = insertBefore as number;

    if (dataRow == null || dataRow === iBefore - 1) return;

    const droppedRowIndex = getDroppedRowIndex(iBefore);
    if (droppedRowIndex == null) return;

    const rowEls = getRowElementsSafe(dataRow, droppedRowIndex);
    if (!rowEls) return;

    const { movedEl, droppedEl } = rowEls;
    const movedElTop = (movedEl as HTMLElement).getBoundingClientRect().top;

    const rowRect = (droppedEl as HTMLElement).getBoundingClientRect();
    const rowRectTop = rowRect.top;

    const droppedRowData = dataRows[droppedRowIndex];
    const movedRowData = dataRows[dataRow];

    const hide = shouldHideGuide({
      movedElTop,
      rowRectTop,
      droppedRowData,
      movedRowData,
      currentTop,
      droppedEl,
      insertBefore: iBefore,
    });

    if (hide) {
      setGuide(reorderGuide, false, false);
      return;
    }

    const midpoint = isMidpointHit(currentTop, droppedEl);
    setGuide(reorderGuide, true, midpoint);
  };

  useEffect(() => {
    handleDrag();
  }, [top]);

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    const dv = gridRef.current?.dataView;
    if (!grid || !dv) return;

    const KEY = "disabled-rows";

    const applyDisabledStyles = () => {
      grid.removeCellCssStyles(KEY);

      if (!Array.isArray(props.disabledIds) || !props.disabledIds.length) return;

      const cols = grid.getColumns();
      const styles: Record<number, Record<string, string>> = {};

      for (const id of props.disabledIds) {
        const rowIdx = dv.getRowById(String(id));
        if (rowIdx == null || rowIdx < 0) continue;

        styles[rowIdx] = {};
        for (const col of cols) {
          styles[rowIdx][col.id] = "slick-disabled-row";
        }
      }

      grid.setCellCssStyles(KEY, styles);
    };

    applyDisabledStyles();

    const rerun = () => applyDisabledStyles();
    dv.onRowsChanged.subscribe(rerun);
    dv.onRowCountChanged.subscribe(rerun);

    return () => {
      grid.removeCellCssStyles(KEY);
      dv.onRowsChanged.unsubscribe(rerun);
      dv.onRowCountChanged.unsubscribe(rerun);
    };
  }, [props.disabledIds]);

  const onSortCallback = useCallback(
    (_e: unknown, args: any) => {
      if (!props.disableSorting) {
        const columnId = String(args?.columnId ?? "");
        const sortAsc = Boolean(args?.sortAsc);
        setSortingDetails({ columnId, sortAsc });
      }
    },
    [props.disableSorting],
  );

  const reactGridReady = (reactGrid: SlickgridReactInstance) => {
    (gridRef as any).current = reactGrid as SlickgridReactInstanceX<T>;

    const exportService = (reactGrid.extensionService as any).sharedService?.externalRegisteredResources?.find(
      (service: any) => service.className === "ExcelExportService",
    );
    if (exportService) setExcelExportExtension(exportService);

    setGridMounted(true);
    reactGrid.slickGrid?.invalidate();

    if (!props.disableCustomSorting) {
      reactGrid.slickGrid.onSort.subscribe(onSortCallback);
    }

    specialColumnRef.current = reactGrid.slickGrid
      .getColumns()
      .filter((c: any) => ["_checkbox_selector", "_move"].includes(c.id))
      .map(mapSpecialColumn) as unknown as ColumnDefinition<T>[];

    if (props.enableWrapText) {
      setTimeout(() => {
        handleWrapUpdate();
      }, INITIAL_WRAP_RUN_DELAY_MS);
    }

    if (props.treeGrid) {
      (reactGrid as any)?.filterService?.disableFilterFunctionality?.();
    }
    reactGrid.slickGrid.onColumnsReordered.subscribe((e, args) => {
      const cols = [...(gridRef.current?.slickGrid.getColumns() ?? [])];
      const corrected = [
        cols.find((c) => c.id === "_checkbox_selector"),
        cols.find((c) => c.id === "_move"),
        ...cols.filter((c) => !["_checkbox_selector", "_move"].includes(c.id as any)),
      ].filter(Boolean);

      gridRef.current?.slickGrid.setColumns(corrected as Column<any>[]);

      if (props.onColumnsReordered) {
        props.onColumnsReordered(e, args);
      }
    });

    props.onReactGridCreated?.(reactGrid);
  };

  const onSelectedRowIdsChanged = (args: any) => {
    setSelectedItems(args.detail.args.selectedRowIds.length);
    props.onSelectedRowIdsChanged?.(args);
  };

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.appliedFilters = appliedFilters;
      if (props.onFilterChange && appliedFilters.length > 0) {
        props.onFilterChange(appliedFilters, filterConditions);
      }
    }
  }, [appliedFilters, filterConditions, props]);

  useEffect(() => {
    if (props.preAppliedFilters && Object.keys(props.preAppliedFilters).length > 0) {
      setAppliedFilters(Object.keys(props.preAppliedFilters));
      setFilterConditions(props.preAppliedFilters);
    }
  }, [props.preAppliedFilters]);

  useEffect(() => {
    if (!isNil(props.showLoader)) {
      setLoaderOnPageChange(!!props.showLoader);
    }
  }, [props.showLoader]);

  useEffect(() => {
    const current = gridRef.current;
    if (current?.deleteRow) {
      current.deleteRow = false;
      current.slickGrid?.setSelectedRows([]);
    }
  }, [gridRef]);

  const getChildData = async (idParam: string | number): Promise<T[]> => {
    const modifiedQuery: QueryShape = { ...props.query };
    const predicate: QueryPredicate = {
      field: String(props.parentIdMapping),
      operator: "equal",
      value: idParam,
      isComplex: false,
      ignoreCase: true,
      ignoreAccent: false,
    };
    modifiedQuery.where = [predicate];
    const response = await axiosWrapper.post(props.remoteDataSource!, { value: modifiedQuery });
    let arr: T[] = [];
    if (props.beforeDataBound) {
      arr = props.beforeDataBound(response);
    } else {
      arr = (response?.data?.result as T[]) ?? [];
    }
    return arr.map((d: any) => {
      if (!d.id) d.id = d._id;
      return d as T;
    });
  };

  useEffect(() => {
    if (gridRef.current && gridMounted && props.surpassedDataset?.length) {
      gridRef.current.dataView.setItems(props.surpassedDataset);
      gridRef.current?.slickGrid?.invalidate();
    }
  }, [props.surpassedDataset, gridMounted, gridRef]);

  const handleRowClickSelection = (rowIndex: number) => {
    const slickGridAny = gridRef.current?.slickGrid as any;
    const selRows: number[] = (slickGridAny?.selectedRows ?? []) as number[];
    if (selRows.includes(rowIndex)) {
      slickGridAny.setSelectedRows(selRows.filter((row: number) => row !== rowIndex));
      return;
    }
    const isMulti = Boolean(gridOptions.multiSelect ?? true);
    const next = isMulti ? [...selRows, rowIndex] : [rowIndex];
    slickGridAny.setSelectedRows(next);
  };

  const handleTreeGridClick = async (rowIndex: number, cellIndex: number) => {
    if (!props.treeGrid || props.disableChildDataFetch) return;
    if (cellIndex !== props.dropdownColumn) return;

    const hasChildMapping = props.hasChildMapping!;
    const row = (gridRef.current as any)?.slickGrid?.data?.items?.[rowIndex];
    if (!row?.[hasChildMapping]) return;

    if (!row.isExpanded) {
      setLoaderOnPageChange(true);
      const indentLevel = row.indentLevel ? row.indentLevel + 1 : 1;
      const dv: any = gridRef.current!.dataView;
      let data: any[] = [];

      if (typeof dv?.getFilteredItems === "function") {
        data = dv.getFilteredItems();
      } else if (typeof dv?.getItems === "function") {
        data = dv.getItems();
      } else {
        data = [];
      }

      let childData = await getChildData(row.id);
      childData = childData.filter((d) => d._id !== row._id);

      const parentIndex = data.findIndex((r: any) => r.id === row.id);

      const childRows = childData.map((child: any) => ({
        ...child,
        indentLevel,
        parentId: row.id,
        [props.hasChildMapping as string]: child[`${hasChildMapping}`] || false,
        __hasChildren: child[`${hasChildMapping}`],
        treeLevel: indentLevel,
        __collapsed: true,
        isExpanded: false,
      }));

      const updatedData = [...data.slice(0, parentIndex + 1), ...childRows, ...data.slice(parentIndex + 1)];
      updatedData[parentIndex] = { ...updatedData[parentIndex], isExpanded: true };

      setDataset([...updatedData]);
      setLoaderOnPageChange(false);
    }

    if (gridRef.current) {
      (gridRef.current as any).dataset = [...dataset];
    }
  };

  const onClick = async (e: any, _args: any) => {
    if (props.selectCheckBoxOnRowClick && !isEditModeRef.current) {
      handleRowClickSelection(e.detail.args.row);
    }
    props.onClick?.(e, _args);
    await handleTreeGridClick(e.detail.args.row, e.detail.args.cell);
  };

  const gridOptions: GridOption = React.useMemo(() => {
    const base: GridOption = {
      enableContextMenu: true,
      enableHeaderButton: true,
      enableGridMenu: props.enableGridMenu ?? true,
      enableColumnPicker: props.enableColumnPicker ?? true,
      showCustomFooter: false,
      enableHeaderMenu: false,
      enableSorting: false,
      enableCellRowSpan: true,
      rowTopOffsetRenderType: "top",
      enableColumnReorder: true,
      enableCellNavigation: !props.isEditMode,
      headerButton: {
        onCommand: (_e: unknown, args: any) =>
          handleFilterIconClick(_e as any, args.column.field, args.column.filterType),
      },
      gridMenu: {
        onCommand: (_e: unknown) => {},
      },
      ...(props.hasChild && {
        preRegisterExternalExtensions: (pubSubService: any) => {
          const rowDetail = new SlickRowDetailView(pubSubService);
          return [{ name: ExtensionName.rowDetailView, instance: rowDetail }];
        },
      }),
      externalResources: [excelExportService],
      enableExcelExport: true,
      enableExcelCopyBuffer: true,
      createPreHeaderPanel: true,
      preHeaderPanelHeight: 28,
      rowHeight: 48,
      multiColumnSort: false,
      multiSelect: props.enableMultiSelect ?? true,
      autoFitColumnsOnFirstLoad: false,
      enableAutoSizeColumns: false,
      enableAutoResize: true,
      autosizeColumnsByCellContentOnFirstLoad: true,
      enableFiltering: Object.hasOwn(props, "enableFiltering") ? !!props.enableFiltering : false,
      enableDraggableGrouping: props.allowGrouping || false,
      enableRowSelection: !props.isEditMode,
      createTopHeaderPanel: true,
      topHeaderPanelHeight: 35,
      enableCheckboxSelector: !props.isEditMode,
      checkboxSelector: { toolTip: "" },
      rowSelectionOptions: { selectActiveRow: false },
      ...(props.treeGrid && {
        enableTreeData: true,
        treeDataOptions: {
          columnId: String(props.initialSortColumnId ?? ""),
          parentPropName: String(props.parentIdMapping ?? ""),
          levelPropName: "treeLevel",
          indentMarginLeft: 25,
          initiallyCollapsed: true,
          initialSort: {
            columnId: String(props.initialSortColumnId ?? ""),
            direction: (props.initialSortDirection as "ASC" | "DESC") || "DESC",
          },
          titleFormatter: props.titleFormatter,
        },
      }),
      ...(props.dragAndDrop && {
        enableRowMoveManager: true,
        rowMoveManager: {
          columnIndexPosition: 0,
          cancelEditOnDrag: true,
          disableRowSelection: true,
          hideRowMoveShadow: false,
          onBeforeMoveRows,
          onMoveRows: (e: any, args: any) => {
            void onMoveRows(e, args);
          },
        },
      }),
      autoResize: {
        container: `#${id.replaceAll(":", "_")}`,
        resizeDetection: "container",
        calculateAvailableSizeBy: "container",
      },
      autoHeight: !!props.hasChild,
      ...props.gridOptions,
    };

    if (props.allowGrouping) {
      (base as any).draggableGrouping = {
        dropPlaceHolderText: "Drop a column header here to group by the column",
        deleteIconCssClass: "mdi mdi-close text-color-danger",
        sortAscIconCssClass: "mdi mdi-arrow-up",
        sortDescIconCssClass: "mdi mdi-arrow-down",
        onGroupChanged: props.onGroupChanged,
        onExtensionRegistered: props.onExtensionRegistered,
      };
    }

    return base;
  }, [excelExportService, id, props]);

  return (
    <div dir="ltr" className="h-full min-w-full cube-slick-grid">
      {!allPrerequisiteLoaded && (
        <div className="w-full pt-4 bg-white">
          <Shimmer />
        </div>
      )}

      {allPrerequisiteLoaded && (
        <div className="flex flex-col h-full min-w-full">
          {props.toolbar && (
            <ToolBar
              toolbar={props.toolbar || []}
              toolbarClickHandler={props.toolbarClickHandler}
              showToolbarBorder={props.showToolbarBorder}
              enableColumnChooser={props.enableColumnChooser}
              columnDefinitions={updatedColumnDefinitions as any}
              callbackForVisibleColumnsUpdate={props.callbackForVisibleColumnsUpdate as any}
              ref={gridRef as unknown as React.Ref<any>}
              height={
                typeof props.toolbarHeight === "string" ? Number.parseFloat(props.toolbarHeight) : props.toolbarHeight
              }
              propertiesPanel={props.propertiesPanel}
              excelExportExtension={excelExportExtension}
              excelExport={!!props.excelExport}
            />
          )}

          <div
            id={id.replaceAll(":", "_")}
            className={"relative flex h-full min-w-full overflow-auto" + (cssClasses ? " " + cssClasses : "")}
          >
            <SlickgridReact
              {...(props as any)}
              {...(!props.datasetHierarchical && !props.surpassedDataset ? { dataset } : {})}
              gridId={`${id.replaceAll(":", "_")}_grid18`}
              gridOptions={gridOptions}
              onReactGridCreated={($event: { detail: SlickgridReactInstance }) => reactGridReady($event.detail)}
              columnDefinitions={updatedColumnDefinitions as any}
              setTotalItemCount={setTotalItemCount}
              onClick={onClick}
              onSelectedRowIdsChanged={onSelectedRowIdsChanged}
              {...(props.infiniteScroll ? { onScroll: handleOnScroll } : {})}
              onAfterSetColumns={() => {
                if (gridRef.current) {
                  if (props.handleUpdateCurrentView) {
                    props.handleUpdateCurrentView({
                      columns: gridRef.current.slickGrid.getColumns().map((c: any) => c.id),
                    });
                  }
                  gridRef.current.dataView.refresh();
                }
              }}
            />
            {loaderOnPageChange && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--surface-white)] opacity-70 pointer-events-auto">
                <div className="flex items-center justify-center">
                  <CircularProgress />
                </div>
              </div>
            )}
          </div>

          {showPageCount && (
            <div className="!h-[50px] w-full flex items-center justify-between p-3 border-b sticky bottom-0 bg-white shadow-md">
              <PagerComponent
                ref={gridRef as unknown as React.Ref<HTMLDivElement>}
                pageSize={pageSize}
                currentPage={currentPage}
                totalItemCount={totalItemCount}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                totalPages={totalPages}
                allowPagination={!!props.allowPagination}
                selectedItems={selectedItems}
                currentLoadedCount={currentLoadedCount}
                infiniteScroll={props.infiniteScroll}
              />
            </div>
          )}

          <FilterComponent
            currentColumnId={currentColumnId}
            setCurrentColumnId={setCurrentColumnId}
            setFilterPredicates={setFilterPredicates}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            filterType={filterType || undefined}
            filterOpened={filterOpened}
            setFilterOpened={setFilterOpened}
            datasetForFetchingOptions={
              appliedFilters.length === 0 || (appliedFilters.length === 1 && appliedFilters[0] === currentColumnId)
                ? (props.dataset ?? [])
                : (dataset ?? [])
            }
            setAppliedFilters={setAppliedFilters}
            appliedFilters={appliedFilters}
            columnDefinitions={props.columnDefinitions as any}
            beforeDataBound={props.beforeDataBound}
            localData={!props.remoteDataSource}
            ref={gridRef}
            remoteDataSource={props.remoteDataSource}
            query={props.query}
            filterConditions={filterConditions}
            setFilterConditions={setFilterConditions}
            getUpdatedFilterConditions={getUpdatedFilterConditions}
            applyFilter={applyFilterLocal as any}
            preAppliedFilters={props.preAppliedFilters}
            onFilterConditionsChange={props.onFilterChange}
          />
        </div>
      )}

      {loaderForInfiniteScroll && (
        <output
          aria-live="polite"
          className="
            absolute bottom-10 start-1/2 -translate-x-1/2
            inline-flex items-center justify-center gap-2
            rounded-full bg-white/90 px-4 py-2
            text-lg font-medium text-slate-800
            shadow-lg shadow-black/5 ring-1 ring-slate-900/5 backdrop-blur-sm
            dark:bg-slate-800/90 dark:text-slate-200 dark:ring-white/10
          "
        >
          <CircularProgress size={18} thickness={5} sx={{ color: "primary.main" }} />
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            <Text>{t("Loading more…")}</Text>
          </span>
        </output>
      )}
    </div>
  );
} as any);

const SlickGrid = forwardRef<SlickgridReactInstanceX<GridRowBase>, SlickGridProps<GridRowBase>>(
  function SlickGrid(props, ref) {
    return <SlickGridInner {...props} ref={ref} />;
  },
);

export default SlickGrid;
